/** Soft two-tone chime for inbound app notifications (Web Audio — no asset file). */

let audioCtx: AudioContext | null = null
let lastPlayedAt = 0
const COOLDOWN_MS = 900

const getAudioContext = (): AudioContext | null => {
  if (typeof window === 'undefined') return null
  const Ctx =
    window.AudioContext || (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext
  if (!Ctx) return null
  if (!audioCtx) audioCtx = new Ctx()
  return audioCtx
}

export const unlockNotificationAudio = () => {
  const ctx = getAudioContext()
  if (ctx?.state === 'suspended') void ctx.resume()
}

/** Play once; ignores rapid repeats (cable + refetch). */
export const playNotificationSound = () => {
  const now = Date.now()
  if (now - lastPlayedAt < COOLDOWN_MS) return
  lastPlayedAt = now

  try {
    const ctx = getAudioContext()
    if (!ctx) return
    if (ctx.state === 'suspended') void ctx.resume()

    const playTone = (freq: number, start: number, duration: number, peak: number) => {
      const osc = ctx.createOscillator()
      const gain = ctx.createGain()
      osc.type = 'sine'
      osc.frequency.value = freq
      const t0 = ctx.currentTime + start
      gain.gain.setValueAtTime(0.0001, t0)
      gain.gain.linearRampToValueAtTime(peak, t0 + 0.02)
      gain.gain.exponentialRampToValueAtTime(0.0001, t0 + duration)
      osc.connect(gain)
      gain.connect(ctx.destination)
      osc.start(t0)
      osc.stop(t0 + duration + 0.05)
    }

    playTone(880, 0, 0.16, 0.09)
    playTone(1174.66, 0.11, 0.22, 0.07)
  } catch {
    // Autoplay may be blocked until the user interacts with the page.
  }
}

/** Browsers require a user gesture before AudioContext can play. */
if (typeof window !== 'undefined') {
  const unlock = () => {
    unlockNotificationAudio()
    window.removeEventListener('pointerdown', unlock)
    window.removeEventListener('keydown', unlock)
  }
  window.addEventListener('pointerdown', unlock, { once: true })
  window.addEventListener('keydown', unlock, { once: true })
}
